import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  MoonOutlined,
} from '@ant-design/icons'
import InfoTip from '@components/charts/analytics/InfoTip'
import { Card, Flex, Skeleton, theme, Typography } from 'antd'
import { ComponentType, ReactNode } from 'react'
import { Link } from 'react-router-dom'

const { Text } = Typography

type InsightTone = 'danger' | 'info' | 'warning' | 'purple' | 'success'

// Figma "Analytics / Insight card" variants (board 961:331): icon colour and
// its tinted circle.
const TONES: Record<
  InsightTone,
  { color: string; bg: string; Icon: ComponentType<{ style?: object }> }
> = {
  danger: { color: '#ff4d4f', bg: '#fff1f0', Icon: CloseCircleOutlined },
  info: { color: '#1677ff', bg: '#e6f4ff', Icon: ClockCircleOutlined },
  warning: { color: '#fa8c16', bg: '#fff7e6', Icon: CreditCardOutlined },
  purple: { color: '#722ed1', bg: '#f9f0ff', Icon: MoonOutlined },
  success: { color: '#52c41a', bg: '#f6ffed', Icon: CheckCircleOutlined },
}

interface Props {
  tone: InsightTone
  /** Bold line, count included ("8 failed renewals not recovered"). */
  headline: ReactNode
  tooltip: string
  /** Muted line under the headline. */
  detail?: ReactNode
  /** Primary-colour link, bottom aligned. Omitted in the empty (success) state. */
  linkLabel?: string
  linkTo?: string
  isLoading?: boolean
}

/** Needs-attention card: tinted icon, headline with info tooltip, detail line, link. */
const InsightCard = ({
  tone,
  headline,
  tooltip,
  detail,
  linkLabel,
  linkTo,
  isLoading,
}: Props) => {
  const { token } = theme.useToken()
  const { color, bg, Icon } = TONES[tone]

  return (
    <Card
      variant="borderless"
      style={{ height: '100%', borderRadius: 8 }}
      styles={{ body: { padding: '20px 24px', height: '100%' } }}
    >
      <Flex gap={12} align="flex-start" style={{ height: '100%' }}>
        <Flex
          align="center"
          justify="center"
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            background: bg,
            flex: 'none',
          }}
        >
          <Icon style={{ color, fontSize: 18 }} />
        </Flex>
        <Flex
          vertical
          justify="space-between"
          gap={16}
          style={{ flex: 1, minWidth: 0, height: '100%' }}
        >
          {isLoading ? (
            <Skeleton active paragraph={{ rows: 1 }} title={false} />
          ) : (
            <Flex vertical gap={8}>
              <Flex align="center" gap={6}>
                <Text strong style={{ fontSize: 14 }}>
                  {headline}
                </Text>
                <InfoTip
                  title={tooltip}
                  style={{ color: token.colorTextTertiary, fontSize: 14 }}
                />
              </Flex>
              {detail && (
                <Text style={{ fontSize: 14, color: token.colorTextSecondary }}>
                  {detail}
                </Text>
              )}
            </Flex>
          )}
          {!isLoading && linkTo && linkLabel && (
            // Primary colour comes from the theme's link colour.
            <Link to={linkTo} style={{ fontSize: 14 }}>
              {linkLabel} →
            </Link>
          )}
        </Flex>
      </Flex>
    </Card>
  )
}

export default InsightCard
export type { InsightTone }
